import {
    GalleryGrid as AssetLibraryGalleryGrid,
    Skeleton,
} from '@deltasierra/react-components-core';
import { WatchElementRefType } from '@deltasierra/react-hooks-common';
import { Translations } from '@deltasierra/translations-react';
import { MouseEvent, useMemo } from 'react';
import { AssetLibraryGalleryEmpty } from './AssetLibraryGalleryEmpty';
import { AssetLibraryGalleryGridItem } from './AssetLibraryGalleryGridItem';
import { ASSET_GALLERY_GRID_SKELETON, AssetType } from './constants';
import { Asset, AssetWithDimensions } from './types';

type AssetLibraryGalleryGridContainerProps = {
    assets: Asset[];
    dataCy?: string;
    isAssetsError: boolean;
    isLoading: boolean;
    loadMoreElementRef?: WatchElementRefType;
    noAssetsMessage: string;
    onClickAsset?: (asset: Asset) => void;
    onClickAssetPreview?: (asset: Asset) => void;
    onClickDownload?: (value?: { assetId: string }) => void;
    onClickOpenActions?: (event: MouseEvent<HTMLElement>, asset: Asset) => void;
    onDoubleClickFolder?: (folderId: string) => void;
    onSelectAsset?: (asset: Asset) => void;
    selectedAssetIds: string[];
    t: Translations<'AssetLibrary'>;
};

export const AssetLibraryGalleryGridContainer = ({
    assets,
    dataCy,
    isAssetsError,
    isLoading,
    loadMoreElementRef,
    noAssetsMessage,
    onClickAsset,
    onClickAssetPreview,
    onClickDownload,
    onClickOpenActions,
    onDoubleClickFolder,
    onSelectAsset,
    selectedAssetIds,
    t,
}: AssetLibraryGalleryGridContainerProps): JSX.Element => {
    const isEmpty = assets.length === 0 && !isLoading;
    const itemsWithDimensions: AssetWithDimensions[] = useMemo(() => {
        let items: Asset[] | AssetWithDimensions[] = assets;
        if (isLoading) {
            items = new Array(10).fill(ASSET_GALLERY_GRID_SKELETON).map((item, index) => ({
                ...item,
                id: `skeleton-${index}`,
            }));
        }
        return items.map(item => ({
            ...item,
            dimensions:
                item.type === AssetType.Asset && item.thumbnailDimensions
                    ? item.thumbnailDimensions
                    : { height: 222, width: 180 },
        }));
    }, [assets, isLoading]);

    if (isAssetsError) {
        return (
            <AssetLibraryGalleryEmpty
                message={`
                    ${t('We are unable to retrieve these assets at this time, please try again soon')}.
                    ${t('If you are still unable to see any assets here, please contact support')}.
                `}
            />
        );
    }

    return isEmpty ? (
        <AssetLibraryGalleryEmpty message={noAssetsMessage} />
    ) : (
        <AssetLibraryGalleryGrid
            data={itemsWithDimensions}
            dataCy={dataCy}
            itemMinWidth={75}
            loadMoreElement={
                !!loadMoreElementRef && <Skeleton height={300} ref={loadMoreElementRef('moreAssets')} width="100%" />
            }
            rowMaxHeight={300}
            rowMinHeight={200}
            spacing={10}
        >
            {asset => (
                <AssetLibraryGalleryGridItem
                    asset={asset}
                    isLoading={isLoading}
                    isSelected={selectedAssetIds.includes(asset.id)}
                    t={t}
                    onClickAsset={onClickAsset}
                    onClickAssetPreview={onClickAssetPreview}
                    onClickDownload={onClickDownload}
                    onClickOpenActions={onClickOpenActions}
                    onClickSelectItem={onSelectAsset}
                    onDoubleClickFolder={onDoubleClickFolder}
                />
            )}
        </AssetLibraryGalleryGrid>
    );
};

AssetLibraryGalleryGridContainer.displayName = 'AssetLibraryGalleryGridContainer';
